/**
 * useReportData Hook
 * React Query hooks for fetching report data
 *
 * Story 2.1: Executive Summary Cards
 */

import { useQuery } from '@tanstack/react-query';
import { reportApi } from '../services/reportApi';
import type {
  ReportScopeInfo,
  DateRangeParams,
  ReportFilterParams,
  ExecutiveSummary,
  StatusDistributionItem,
  DepartmentProgress,
  EngagementMetrics,
  ReportFilterMetadata,
  UserActivityResult,
  UserActivityParams,
  CheckInCompliance,
  CollaborationMetrics,
  FeatureUsageData,
  ActiveUsersData,
  ProgressDistributionData,
  AtRiskOKRsData,
  StagnantOKRsData,
  CompletionPredictionData,
  OKRHealthSummary,
  // Phase 5 types
  ActivationFunnelData,
  TimeToFirstOKRData,
  FeatureDiscoveryData,
  RetentionMetricsData,
  UserAdoptionSummary,
} from '../services/reportApi';

/** True when any department / team / owner filter value is set. */
function hasFilterValues(filters?: ReportFilterParams): boolean {
  return Boolean(filters?.departmentIds?.length || filters?.teamIds?.length || filters?.ownerIds?.length);
}

// Query keys for cache management
export const reportQueryKeys = {
  all: ['reports'] as const,
  filterOptions: () => [...reportQueryKeys.all, 'filter-options'] as const,
  executiveSummary: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'executive-summary', dateRange, filters] as const,
  statusDistribution: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'status-distribution', dateRange, filters] as const,
  progressByDepartment: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'progress-by-department', dateRange, filters] as const,
  engagementMetrics: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'engagement-metrics', dateRange, filters] as const,
  userActivity: (dateRange: DateRangeParams, params?: UserActivityParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'user-activity', dateRange, params, filters] as const,
  checkInCompliance: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'check-in-compliance', dateRange, filters] as const,
  collaborationMetrics: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'collaboration-metrics', dateRange, filters] as const,
  featureUsage: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'feature-usage', dateRange, filters] as const,
  activeUsers: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'active-users', dateRange, filters] as const,
  // OKR Health Report keys (Phase 4)
  okrHealthSummary: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'okr-health-summary', dateRange, filters] as const,
  progressDistribution: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'progress-distribution', dateRange, filters] as const,
  atRiskOKRs: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'at-risk-okrs', dateRange, filters] as const,
  stagnantOKRs: (
    dateRange: DateRangeParams,
    filters?: ReportFilterParams,
    thresholdDays?: number
  ) => [...reportQueryKeys.all, 'stagnant-okrs', dateRange, filters, thresholdDays] as const,
  completionPrediction: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'completion-prediction', dateRange, filters] as const,
  // User Adoption Report keys (Phase 5)
  userAdoptionSummary: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'user-adoption-summary', dateRange, filters] as const,
  activationFunnel: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'activation-funnel', dateRange, filters] as const,
  timeToFirstOKR: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'time-to-first-okr', dateRange, filters] as const,
  featureDiscovery: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'feature-discovery', dateRange, filters] as const,
  retentionMetrics: (dateRange: DateRangeParams, filters?: ReportFilterParams) =>
    [...reportQueryKeys.all, 'retention-metrics', dateRange, filters] as const,
};

// ============================================
// Report scope (Item B / Change C2)
// ============================================

/**
 * Item B — this module is the single place report scope is interpreted.
 *
 * The API returns a raw `scope` field on every scoped response (see
 * `ReportScopeInfo` in `services/reportApi.ts`). **No component may read that
 * field off an API response.** Components receive a `ResolvedScope` — already
 * decided, already worded — so the rule for what a figure covers lives here
 * and cannot drift card by card. That is the same single-place discipline the
 * backend applies in `utils/reportScope.ts`.
 */
export type ReportScopeKind = 'unscoped' | 'team' | 'organization';

export interface ResolvedScope {
  /**
   * `unscoped` — the caller reads the whole tenant and the API sends no scope
   * field at all (an `account_admin`). Render no label: an admin's page must
   * look exactly as it did before Item B.
   * `team` — the figure covers the caller plus their active direct reports.
   * `organization` — the figure is tenant-wide *even though the caller is
   * scoped*. Real, and deliberate: DAU/WAU/MAU and feature adoption are
   * meaningless at team size, so those cards must NOT inherit a team label.
   */
  kind: ReportScopeKind;
  /** Owners the caller's scope covers: self + active direct reports. 0 when unscoped. */
  ownerCount: number;
  /** Badge text, or null when there is nothing honest to say. */
  label: string | null;
  /** A sentence naming whose data this is, or null when unscoped. */
  description: string | null;
  /**
   * Spec § 4.5 (C4): a scoped caller who manages nobody. Not an error and not
   * an empty payload — they see their own data, and the UI must say so rather
   * than render a bare 0.
   */
  isSelfOnly: boolean;
}

const UNSCOPED: ResolvedScope = {
  kind: 'unscoped',
  ownerCount: 0,
  label: null,
  description: null,
  isSelfOnly: false,
};

/**
 * Turn the API's raw scope field into something renderable.
 *
 * `undefined` in means an unrestricted caller — never a fallback, never a
 * guess from the client-side role. The server decides; this only words it.
 */
export function describeReportScope(scope: ReportScopeInfo | undefined): ResolvedScope {
  if (!scope) return UNSCOPED;

  if (scope.type === 'organization') {
    return {
      kind: 'organization',
      ownerCount: scope.ownerCount,
      label: 'Organization-wide',
      description: 'This figure covers the whole organization, not just your team.',
      isSelfOnly: false,
    };
  }

  // A team scope always contains the caller's own id, so ownerCount <= 1 means
  // "manages nobody" rather than "empty team".
  const reportCount = Math.max(scope.ownerCount - 1, 0);
  if (reportCount === 0) {
    return {
      kind: 'team',
      ownerCount: scope.ownerCount,
      label: 'Your data',
      description: 'No direct reports yet — showing your own data.',
      isSelfOnly: true,
    };
  }

  return {
    kind: 'team',
    ownerCount: scope.ownerCount,
    label: 'Your team',
    description: `Showing you and your ${reportCount} direct report${reportCount === 1 ? '' : 's'}.`,
    isSelfOnly: false,
  };
}

/**
 * The organization-wide counterpart of a caller's scope.
 *
 * Exists for `/engagement/collaboration`, which is PARTIALLY scoped: one
 * response carries team-scoped lists AND organization-wide totals, but only
 * one `scope` field. An unscoped caller stays unscoped — a label an
 * account_admin never needed does not appear now.
 */
export function asOrganizationScope(scope: ResolvedScope): ResolvedScope {
  if (scope.kind === 'unscoped') return UNSCOPED;
  return describeReportScope({ type: 'organization', ownerCount: scope.ownerCount });
}

/**
 * Hook to fetch filter options
 */
export function useReportFilterOptions(enabled = true) {
  return useQuery<ReportFilterMetadata>({
    queryKey: reportQueryKeys.filterOptions(),
    queryFn: () => reportApi.getFilterOptions(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * The caller's own report scope, for the page-level indicator.
 *
 * Read from `/reports/filter-options` because scope is a property of the
 * caller, not of a tab: one cheap, long-cached request answers it for the
 * whole page, and it comes from the API rather than from a client-side guess
 * at what a role implies. Every other endpoint reports the same scope.
 */
export function useReportScope(enabled = true) {
  const filterOptions = useReportFilterOptions(enabled);

  return {
    scope: describeReportScope(filterOptions.data?.scope),
    isLoading: filterOptions.isLoading,
  };
}

/**
 * Hook to fetch executive summary
 */
export function useExecutiveSummary(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<ExecutiveSummary>({
    queryKey: reportQueryKeys.executiveSummary(dateRange, filters),
    queryFn: () => reportApi.getExecutiveSummary(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });
}

/**
 * Hook to fetch status distribution
 */
export function useStatusDistribution(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<StatusDistributionItem[]>({
    queryKey: reportQueryKeys.statusDistribution(dateRange, filters),
    queryFn: () => reportApi.getStatusDistribution(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch progress by department
 */
export function useProgressByDepartment(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<DepartmentProgress[]>({
    queryKey: reportQueryKeys.progressByDepartment(dateRange, filters),
    queryFn: () => reportApi.getProgressByDepartment(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch engagement metrics
 */
export function useEngagementMetrics(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<EngagementMetrics>({
    queryKey: reportQueryKeys.engagementMetrics(dateRange, filters),
    queryFn: () => reportApi.getEngagementMetrics(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Combined hook for all executive dashboard data
 */
export function useExecutiveDashboardData(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams
) {
  const summary = useExecutiveSummary(dateRange, filters);
  const statusDistribution = useStatusDistribution(dateRange, filters);
  const progressByDepartment = useProgressByDepartment(dateRange, filters);

  return {
    summary,
    statusDistribution,
    progressByDepartment,
    isLoading: summary.isLoading || statusDistribution.isLoading || progressByDepartment.isLoading,
    isError: summary.isError || statusDistribution.isError || progressByDepartment.isError,
    error: summary.error || statusDistribution.error || progressByDepartment.error,
  };
}

/**
 * Hook to fetch user activity
 */
export function useUserActivity(
  dateRange: DateRangeParams,
  params?: UserActivityParams,
  enabled = true,
  filters?: ReportFilterParams
) {
  return useQuery<UserActivityResult>({
    queryKey: reportQueryKeys.userActivity(dateRange, params, filters),
    queryFn: () => reportApi.getUserActivity(dateRange, params, filters),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes for frequently changing data
  });
}

/**
 * User activity, with its scope already resolved.
 *
 * A thin wrapper rather than a field on `useUserActivity`'s result: react-query
 * results use tracked getters, so spreading one would defeat its re-render
 * optimisation. This exists so the dashboard never reads `data.scope` itself —
 * every reading of that field happens in `describeReportScope`.
 */
export function useUserActivityWithScope(
  dateRange: DateRangeParams,
  params?: UserActivityParams,
  enabled = true,
  filters?: ReportFilterParams
) {
  const query = useUserActivity(dateRange, params, enabled, filters);
  return { query, scope: describeReportScope(query.data?.scope) };
}

/**
 * Hook to fetch check-in compliance
 */
export function useCheckInCompliance(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<CheckInCompliance>({
    queryKey: reportQueryKeys.checkInCompliance(dateRange, filters),
    queryFn: () => reportApi.getCheckInCompliance(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch collaboration metrics
 */
export function useCollaborationMetrics(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<CollaborationMetrics>({
    queryKey: reportQueryKeys.collaborationMetrics(dateRange, filters),
    queryFn: () => reportApi.getCollaborationMetrics(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch feature usage statistics
 */
export function useFeatureUsage(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<FeatureUsageData>({
    queryKey: reportQueryKeys.featureUsage(dateRange, filters),
    queryFn: () => reportApi.getFeatureUsage(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch active users metrics (DAU/WAU/MAU)
 */
export function useActiveUsers(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<ActiveUsersData>({
    queryKey: reportQueryKeys.activeUsers(dateRange, filters),
    queryFn: () => reportApi.getActiveUsers(dateRange, filters),
    enabled,
    staleTime: 2 * 60 * 1000, // More frequently updated data
  });
}

/**
 * Combined hook for all engagement report data
 */
export function useEngagementReportData(dateRange: DateRangeParams, filters?: ReportFilterParams) {
  const checkInCompliance = useCheckInCompliance(dateRange, filters);
  const collaborationMetrics = useCollaborationMetrics(dateRange, filters);
  const featureUsage = useFeatureUsage(dateRange, filters);
  const activeUsers = useActiveUsers(dateRange, filters);
  // Under a department / team / owner filter the backend narrows the collaboration TOTALS too,
  // so they must not be labelled organisation-wide then.
  const filtered = hasFilterValues(filters);

  return {
    checkInCompliance,
    collaborationMetrics,
    featureUsage,
    activeUsers,
    /**
     * Resolved per endpoint, NOT once for the tab. `featureUsage` and
     * `activeUsers` come back `organization` for the very same manager whose
     * other two cards come back `team` — a single tab-wide label would
     * mislabel two of the four cards.
     */
    scopes: {
      checkInCompliance: describeReportScope(checkInCompliance.data?.scope),
      /** `topContributors` + `mostDiscussedOKRs` — the scoped half of the card. */
      collaborationLists: describeReportScope(collaborationMetrics.data?.scope),
      /** `totalComments` + `avgCommentsPerOKR` — org-wide for a scoped caller, unless filtered. */
      collaborationTotals: filtered
        ? describeReportScope(collaborationMetrics.data?.scope)
        : asOrganizationScope(describeReportScope(collaborationMetrics.data?.scope)),
      featureUsage: describeReportScope(featureUsage.data?.scope),
      activeUsers: describeReportScope(activeUsers.data?.scope),
    },
    isLoading: checkInCompliance.isLoading || collaborationMetrics.isLoading || featureUsage.isLoading || activeUsers.isLoading,
    isError: checkInCompliance.isError || collaborationMetrics.isError || featureUsage.isError || activeUsers.isError,
    error: checkInCompliance.error || collaborationMetrics.error || featureUsage.error || activeUsers.error,
  };
}

// ============================================
// OKR Health Report Hooks (Phase 4)
// ============================================

/**
 * Hook to fetch OKR health summary
 */
export function useOKRHealthSummary(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<OKRHealthSummary>({
    queryKey: reportQueryKeys.okrHealthSummary(dateRange, filters),
    queryFn: () => reportApi.getOKRHealthSummary(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch progress distribution
 */
export function useProgressDistribution(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<ProgressDistributionData>({
    queryKey: reportQueryKeys.progressDistribution(dateRange, filters),
    queryFn: () => reportApi.getProgressDistribution(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch at-risk OKRs
 */
export function useAtRiskOKRs(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<AtRiskOKRsData>({
    queryKey: reportQueryKeys.atRiskOKRs(dateRange, filters),
    queryFn: () => reportApi.getAtRiskOKRs(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch stagnant OKRs
 */
export function useStagnantOKRs(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  thresholdDays: number = 14,
  enabled = true
) {
  return useQuery<StagnantOKRsData>({
    queryKey: reportQueryKeys.stagnantOKRs(dateRange, filters, thresholdDays),
    queryFn: () => reportApi.getStagnantOKRs(dateRange, filters, thresholdDays),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch completion prediction
 */
export function useCompletionPrediction(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<CompletionPredictionData>({
    queryKey: reportQueryKeys.completionPrediction(dateRange, filters),
    queryFn: () => reportApi.getCompletionPrediction(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Combined hook for all OKR health report data
 */
export function useOKRHealthReportData(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  stagnantThresholdDays: number = 14
) {
  const healthSummary = useOKRHealthSummary(dateRange, filters);
  const progressDistribution = useProgressDistribution(dateRange, filters);
  const atRiskOKRs = useAtRiskOKRs(dateRange, filters);
  // Filters were never passed here, so a department or owner pick narrowed every
  // health card except this one.
  const stagnantOKRs = useStagnantOKRs(dateRange, filters, stagnantThresholdDays);
  const completionPrediction = useCompletionPrediction(dateRange, filters);

  return {
    healthSummary,
    progressDistribution,
    atRiskOKRs,
    stagnantOKRs,
    completionPrediction,
    /** All five are team-scoped for a scoped caller; resolved here anyway so
     *  no health component ever reads `scope` off a response itself. */
    scopes: {
      healthSummary: describeReportScope(healthSummary.data?.scope),
      progressDistribution: describeReportScope(progressDistribution.data?.scope),
      atRiskOKRs: describeReportScope(atRiskOKRs.data?.scope),
      stagnantOKRs: describeReportScope(stagnantOKRs.data?.scope),
      completionPrediction: describeReportScope(completionPrediction.data?.scope),
    },
    isLoading:
      healthSummary.isLoading ||
      progressDistribution.isLoading ||
      atRiskOKRs.isLoading ||
      stagnantOKRs.isLoading ||
      completionPrediction.isLoading,
    isError:
      healthSummary.isError ||
      progressDistribution.isError ||
      atRiskOKRs.isError ||
      stagnantOKRs.isError ||
      completionPrediction.isError,
    error:
      healthSummary.error ||
      progressDistribution.error ||
      atRiskOKRs.error ||
      stagnantOKRs.error ||
      completionPrediction.error,
  };
}

// ============================================
// User Adoption Report Hooks (Phase 5)
// ============================================

/**
 * Hook to fetch user adoption summary
 */
export function useUserAdoptionSummary(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<UserAdoptionSummary>({
    queryKey: reportQueryKeys.userAdoptionSummary(dateRange, filters),
    queryFn: () => reportApi.getUserAdoptionSummary(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch activation funnel data
 */
export function useActivationFunnel(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<ActivationFunnelData>({
    queryKey: reportQueryKeys.activationFunnel(dateRange, filters),
    queryFn: () => reportApi.getActivationFunnel(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch time to first OKR data
 */
export function useTimeToFirstOKR(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<TimeToFirstOKRData>({
    queryKey: reportQueryKeys.timeToFirstOKR(dateRange, filters),
    queryFn: () => reportApi.getTimeToFirstOKR(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch feature discovery data
 */
export function useFeatureDiscovery(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<FeatureDiscoveryData>({
    queryKey: reportQueryKeys.featureDiscovery(dateRange, filters),
    queryFn: () => reportApi.getFeatureDiscovery(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch retention metrics
 */
export function useRetentionMetrics(
  dateRange: DateRangeParams,
  filters?: ReportFilterParams,
  enabled = true
) {
  return useQuery<RetentionMetricsData>({
    queryKey: reportQueryKeys.retentionMetrics(dateRange, filters),
    queryFn: () => reportApi.getRetentionMetrics(dateRange, filters),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Combined hook for all user adoption report data
 */
export function useUserAdoptionReportData(dateRange: DateRangeParams, filters?: ReportFilterParams) {
  const adoptionSummary = useUserAdoptionSummary(dateRange, filters);
  const activationFunnel = useActivationFunnel(dateRange, filters);
  const timeToFirstOKR = useTimeToFirstOKR(dateRange, filters);
  const featureDiscovery = useFeatureDiscovery(dateRange, filters);
  const retentionMetrics = useRetentionMetrics(dateRange, filters);

  return {
    adoptionSummary,
    activationFunnel,
    timeToFirstOKR,
    featureDiscovery,
    retentionMetrics,
    isLoading:
      adoptionSummary.isLoading ||
      activationFunnel.isLoading ||
      timeToFirstOKR.isLoading ||
      featureDiscovery.isLoading ||
      retentionMetrics.isLoading,
    isError:
      adoptionSummary.isError ||
      activationFunnel.isError ||
      timeToFirstOKR.isError ||
      featureDiscovery.isError ||
      retentionMetrics.isError,
    error:
      adoptionSummary.error ||
      activationFunnel.error ||
      timeToFirstOKR.error ||
      featureDiscovery.error ||
      retentionMetrics.error,
  };
}
